import _ from 'lodash';
import { DOM } from '../../browser-helpers/dom-factory';
import { Toast } from '../../ui-common/ui-toasts/toast';
import { Dialog } from '../../ui-common/ui-dialogs/dialog';
import { Events } from '../../browser-constants/events';
import { BaseTool } from '../base-tool';
import { LogManager } from '../../toolbar-managers/log-manager/log-manager';
import { ShortcutKeys } from '../../browser-constants/shortcut-keys';
import { isShortcutKeyOnly } from '../../browser-helpers/is-shortcut-key-only';
import { TranslationManager } from '../../toolbar-managers/translation-manager/translation-manager';
import { SvgPaths, getSvgIcon } from '../../ui-icons/get-svg-icon/get-svg-icon';

const FILENAME = 'help-tool.js';
const CLASS__TOOL_BUTTON = 'oltb-tool-button';
const I18N__BASE = 'tools.helpTool';

const DefaultOptions = Object.freeze({
  url: 'https://github.com/qulle/oltb',
  target: '_blank',
  onInitiated: undefined,
  onClicked: undefined,
});

/**
 * About:
 * Open help or documentation pages
 *
 * Description:
 * Open documentation for the functions of the Map or your application as a whole, corresponds to F1 in many computer applications.
 */
class HelpTool extends BaseTool {
  constructor(options = {}) {
    super({
      filename: FILENAME,
    });

    const icon = getSvgIcon({
      path: SvgPaths.questionCircle.stroked,
      class: `${CLASS__TOOL_BUTTON}__icon`,
    });

    const i18n = TranslationManager.get(I18N__BASE);
    const button = DOM.createElement({
      element: 'button',
      html: icon,
      class: CLASS__TOOL_BUTTON,
      attributes: {
        type: 'button',
        'data-tippy-content': ShortcutKeys.helpTool ? `${i18n.title} (${ShortcutKeys.helpTool})` : `${i18n.title}`,
        'data-tippy-content-post': `(${ShortcutKeys.helpTool})`,
        'data-oltb-i18n': `${I18N__BASE}.title`,
      },
      listeners: {
        click: this.onClickTool.bind(this),
      },
    });

    DOM.appendChildren(this.element, [button]);

    this.button = button;
    this.helpDialog = undefined;
    this.options = _.merge(_.cloneDeep(DefaultOptions), options);

    this.attachGlobalListeners();

    // Note:
    // @Consumer callback
    if (this.options.onInitiated) {
      this.options.onInitiated();
    }
  }

  attachGlobalListeners() {
    this.onWindowKeyUpBind = this.#onWindowKeyUp.bind(this);
    window.addEventListener(Events.browser.keyUp, this.onWindowKeyUpBind);
  }

  detachGlobalListeners() {
    window.removeEventListener(Events.browser.keyUp, this.onWindowKeyUpBind);
  }

  //--------------------------------------------------------------------
  // # Section: Overridden
  //--------------------------------------------------------------------
  getName() {
    return super.getName();
  }

  onClickTool(event) {
    super.onClickTool(event);
    this.momentaryActivation();

    // Note:
    // @Consumer callback
    if (this.options.onClicked) {
      this.options.onClicked();
    }
  }

  //--------------------------------------------------------------------
  // # Section: Tool Control
  //--------------------------------------------------------------------
  momentaryActivation() {
    this.askToOpenTabOrWindow();
  }

  //--------------------------------------------------------------------
  // # Section: Browser Events
  //--------------------------------------------------------------------
  #onWindowKeyUp(event) {
    if (isShortcutKeyOnly(event, ShortcutKeys.helpTool)) {
      this.onClickTool();
    }
  }

  //--------------------------------------------------------------------
  // # Section: Ask User
  //--------------------------------------------------------------------
  askToOpenTabOrWindow() {
    if (this.helpDialog) {
      return;
    }

    const i18n = TranslationManager.get(`${I18N__BASE}.dialogs.confirms.openHelp`);
    this.helpDialog = Dialog.confirm({
      title: i18n.title,
      message: i18n.message,
      confirmClass: Dialog.Success,
      confirmText: i18n.confirmText,
      cancelText: i18n.cancelText,
      onConfirm: () => {
        this.doOpenTabOrWindow();
        this.helpDialog = undefined;
      },
      onCancel: () => {
        this.helpDialog = undefined;
      },
    });
  }

  //--------------------------------------------------------------------
  // # Section: Tool DoActions
  //--------------------------------------------------------------------
  doOpenTabOrWindow() {
    try {
      window.open(this.options.url, this.options.target).focus();
    } catch (error) {
      LogManager.logError(FILENAME, 'doOpenTabOrWindow', {
        message: 'Action was restricted by browser settings',
        error,
      });

      Toast.error({
        i18nKey: `${I18N__BASE}.toasts.errors.blockedByBrowser`,
      });
    }
  }
}

export { HelpTool };
